import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api, { API_ENDPOINT } from '@/api';
import { NoticeBox } from '@/common/Notice';
import { CertificateNotice } from '@/courses/CertificateNotice';
import { CourseContent } from '@/courses/CourseContent';
import { CourseHero } from '@/courses/CourseHero';
import { CourseInfoSidebar } from '@/courses/CourseInfoSidebar';
import { CourseProgressIndicator } from '@/courses/CourseProgressIndicator';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { formatString } from '~/utils/stringFormat';

export const Route = createFileRoute('/_app/courses/$courseSlug')({
  component: CourseScreen,
  beforeLoad: () => {
    return {
      noContainer: true,
      noOverflowHidden: true,
    };
  },
});

function CourseScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<
    Record<number, boolean>
  >({});
  const { me, isStudent } = useCurrentUser();
  const { courseSlug } = Route.useParams();
  const language = me?.language ?? 'EN';

  const { data: resolved } = api.courseBySlug.useQuery({
    path: { slug: courseSlug },
    query: { language },
  });

  const courseId = resolved?.id;

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: lessonsData } = api.courseLessons.useQuery(
    { path: { course_id: courseId ?? 0 }, query: { limit: 50 } },
    { enabled: courseId != null },
  );

  /**
   * Every per-viewer literal on this page comes from here. Enrolment is
   * `access_granted_at IS NOT NULL`, which is what `sfwd_lms_has_access`
   * answers in legacy (`learndash-sidebar.php:43`) — the row-existence rule this
   * replaced disagreed on 232 of 1,350 migrated `course_completions` rows (229
   * staff, 3 students). It also answers preview mode: every non-student viewer
   * gets `has_access` true and empty locks (`modules/course-preview/src/
   * Preview.php:12-25`).
   */
  const { data: viewerState } = api.courseViewerState.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { mutateAsync: unlockCourse, isPending: isUnlocking } =
    api.unlockCourse.useMutation();

  const hasAccess = viewerState?.has_access ?? false;
  const isComplete = viewerState?.completed_at != null;
  const progressPct = viewerState?.percent_complete ?? 0;
  const prerequisite = viewerState?.prerequisite ?? null;
  const lockedLessonIds = new Set(viewerState?.locked_lesson_ids ?? []);

  /**
   * The notice needs a download target and the server owns it: `download_url` is
   * the root-relative path of the gated 302 (`certificates/router.py:70`), so
   * this builds no URL of its own. Only fetched once the state read says an
   * award exists — a course with no template answers an empty page, never the
   * viewer's whole scope (`certificates/service.certificates_for_course`).
   */
  const { data: certificates } = api.listCertificates.useQuery(
    { query: { course_id: courseId ?? 0, limit: 1 } },
    {
      enabled: courseId != null && viewerState?.certificate_awarded === true,
    },
  );
  const certificateUrl = certificates?.data[0]?.download_url ?? null;

  const lessons = lessonsData?.data ?? [];
  const lessonsCount = lessonsData?.count ?? 0;
  const topicsCount = lessons.reduce((sum, l) => sum + l.topics_count, 0);

  const mappedLessons = lessons.map((lesson) => ({
    id: lesson.id,
    title: lesson.title,
    slug: lesson.slug,
    topicsCount: lesson.topics_count,
    // An affordance, not a gate — `locked_lesson_ids` is the sequential
    // approximation of legacy's per-user unlock map, whose 1,116 usermeta rows
    // are unmigrated. Legacy renders the same thing: a lock glyph plus the
    // balloon on a row `sfwd_lms_has_access` refuses
    // (`learndash-sidebar.php:130-133`).
    isLocked: lockedLessonIds.has(lesson.id),
  }));

  const allExpanded = mappedLessons.every((_, i) => expandedSections[i]);

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      setExpandedSections({});
    } else {
      const all: Record<number, boolean> = {};
      mappedLessons.forEach((_, i) => {
        all[i] = true;
      });
      setExpandedSections(all);
    }
  };

  const handleUnlock = async () => {
    if (courseId == null) return;
    await unlockCourse({ path: { course_id: courseId } });
    // The unlock stamps `access_granted_at` and creates the progress row, so
    // both reads that drive this page are stale the moment it returns. The
    // response carries the new state, but the lesson rows read `course_progress`
    // separately and would keep the pre-enrolment answer.
    await Promise.all([
      api.courseViewerState.invalidateQueries(),
      api.courseProgress.invalidateQueries(),
    ]);
  };

  // `lessons.course` is the label legacy interpolates twice, lower-cased:
  // `learndash_get_custom_label_lower('course')` at
  // `plugins/sfwd-lms/themes/ld30/templates/modules/messages/prerequisites.php:56-66`.
  const courseLabel = t('lessons.course').toLowerCase();

  const certificateHref =
    viewerState?.certificate_awarded === true && certificateUrl !== null
      ? `${API_ENDPOINT}${certificateUrl}`
      : null;

  // Legacy's alert is what a viewer BLOCKED by a prerequisite sees; an enrolled
  // student has already satisfied it, and preview mode bypasses it outright
  // (`modules/series/src/Course.php:8-9` forces `learndash_prerequities_bypass`).
  // `CourseViewerState` carries the prerequisite course, not whether this
  // student finished it, so the not-enrolled case is the only one where the
  // notice is certainly true.
  const prerequisiteNotice = isStudent && !hasAccess ? prerequisite : null;

  return (
    <Wrapper>
      <CourseHero title={courseDetail?.title ?? ''} />
      <div style={{ overflow: 'visible', padding: '0 20px' }}>
        <ContentWrapper>
          <MainContent>
            {isStudent &&
              (certificateHref !== null ||
                hasAccess ||
                prerequisiteNotice !== null) && (
                <NoticeWrapper>
                  {/* Outside the `!isComplete` branch it used to sit in: an award
                    exists only for a course the student FINISHED, so there it
                    could never render. */}
                  {certificateHref !== null && (
                    <CertificateNotice downloadUrl={certificateHref} />
                  )}
                  {hasAccess && (
                    <CourseProgressIndicator
                      progress={progressPct}
                      lastActivityDate={viewerState?.last_activity_at ?? null}
                      stepsCompleted={viewerState?.topics_completed ?? 0}
                      stepsTotal={viewerState?.topics_total ?? 0}
                      isComplete={isComplete}
                    />
                  )}
                  {prerequisiteNotice !== null && (
                    <NoticeBox icon={faExclamationCircle} bold>
                      {formatString(
                        t('wbs.to-take-this-1'),
                        courseLabel,
                        courseLabel,
                      )}{' '}
                      <Link
                        to="/courses/$courseSlug/"
                        params={{ courseSlug: prerequisiteNotice.slug }}
                      >
                        {prerequisiteNotice.title}
                      </Link>
                    </NoticeBox>
                  )}
                </NoticeWrapper>
              )}
            <CourseDescription
              // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from trusted CMS
              dangerouslySetInnerHTML={{ __html: courseDetail?.content ?? '' }}
            />

            <CourseContent
              courseId={courseId || 0}
              lessons={mappedLessons}
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
              onNavigateToLesson={(lessonSlug) => {
                navigate({
                  to: '/lessons/$lessonSlug/',
                  params: { lessonSlug },
                });
              }}
              onExpandCollapseAll={handleExpandCollapseAll}
              allExpanded={allExpanded}
            />
          </MainContent>

          <CourseInfoSidebar
            imageUrl={courseDetail?.thumbnail ?? ''}
            lessonsCount={lessonsCount}
            topicsCount={topicsCount}
            hasCertificate={courseDetail?.has_certificate ?? false}
            requiresStudyHelper={courseDetail?.study_helper_required ?? false}
            progress={progressPct}
            hasAccess={hasAccess}
            unlockable={viewerState?.unlockable ?? false}
            onlineForbidden={viewerState?.online_forbidden ?? false}
            onlineForbiddenMessage={viewerState?.online_forbidden_message ?? ''}
            onUnlock={handleUnlock}
            isUnlocking={isUnlocking}
          />
        </ContentWrapper>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  background: var(--grey-150);
  min-height: 100vh;
`;

const NoticeWrapper = styled.div`
  margin-bottom: 51px;
`;

const ContentWrapper = styled.div`
  display: grid;
  gap: 40px;
  grid-template-columns: 1fr 360px;
  margin: 0 auto;
  max-width: 1200px;
  padding: 50px 0px 83px 0px;
  position: relative;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CourseDescription = styled.div`
  color: var(--grey-900);
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 58px 0;
`;
